import * as React from 'react';
import { dayjs } from '@/extendedDayjs';
import { useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import Tooltip from 'antd/lib/tooltip';
import { LifecycleEventType, ReleaseCalendarEventType, ReleaseStepStatus, SettingType } from '@/types';
import { ReleaseDelay } from '@/types/releaseDelay';
import { RootState } from '@/reducers/reducers';
import { selectAppTimeFormatSetting, selectEffectiveTimezone } from '@/selectors/selectors';
import {
    createAppSettingsAutomationsRouteWithAutomation,
    createAppSettingsRouteWithSettingType,
} from '@/helpers/routeHelpers';
import { formatDateWithTimezone, formatDelayMinutes } from '@/timeHelpers';
import { releaseDelayAfterAccelerationText } from '@/helpers/displayHelpers';
import { AppRouteComponentMatchParams } from '@/typescriptHelpers';

export const showsReleaseDelay = (
    stepData: { releaseDelay?: ReleaseDelay; plannedReleaseDelayMinutes?: number },
    isDone: boolean,
    stepStatus: ReleaseStepStatus,
) =>
    !!(stepData.releaseDelay || stepData.plannedReleaseDelayMinutes) &&
    !isDone &&
    (stepStatus === ReleaseStepStatus.Ready || stepStatus === ReleaseStepStatus.Pending);

interface ReleaseDelayDescriptionProps {
    orgId: string;
    appId: string;
    releaseDelay?: ReleaseDelay;
    plannedReleaseDelayMinutes?: number;
    isSubmission: boolean;
    automationId?: string;
}

interface ReleaseDelayTooltipProps {
    delayMinutes: number;
    isSubmission: boolean;
    hasStarted: boolean;
    requestedBy?: string;
    settingsRoute?: string;
    children: React.ReactNode;
}

export const ReleaseDelayTooltip: React.FC<ReleaseDelayTooltipProps> = ({
    delayMinutes,
    isSubmission,
    hasStarted,
    requestedBy,
    settingsRoute,
    children,
}) => {
    const { orgIdParam, appIdParam } = useParams<AppRouteComponentMatchParams>();
    const automationSettingsRoute =
        settingsRoute ?? createAppSettingsRouteWithSettingType(orgIdParam, appIdParam, SettingType.Automations);
    const title = (
        <>
            {releaseDelayAfterAccelerationText(delayMinutes, isSubmission, hasStarted)}
            {hasStarted && requestedBy ?
                `, as requested when ${isSubmission ? 'submitting' : 'releasing'}.`
            :   <>
                    {' '}
                    per <Link to={automationSettingsRoute}>automation settings</Link>.
                </>
            }
        </>
    );
    return (
        <Tooltip title={title}>
            <span
                style={{
                    cursor: 'help',
                    textDecorationLine: 'underline',
                    textDecorationStyle: 'dotted',
                    textUnderlineOffset: 3,
                }}
            >
                {children}
            </span>
        </Tooltip>
    );
};

export const ReleaseDelayDescription: React.FC<ReleaseDelayDescriptionProps> = ({
    orgId,
    appId,
    releaseDelay,
    plannedReleaseDelayMinutes,
    isSubmission,
    automationId,
}) => {
    const settingsRoute =
        automationId ?
            createAppSettingsAutomationsRouteWithAutomation(orgId, appId, automationId)
        :   createAppSettingsRouteWithSettingType(orgId, appId, SettingType.Automations);

    if (!releaseDelay) {
        const delayMinutes = plannedReleaseDelayMinutes ?? 0;
        return (
            <>
                Pending {isSubmission ? 'submission' : 'release'}{' '}
                <ReleaseDelayTooltip
                    delayMinutes={delayMinutes}
                    isSubmission={isSubmission}
                    hasStarted={false}
                    settingsRoute={settingsRoute}
                >
                    (after {formatDelayMinutes(delayMinutes)} planned delay)
                </ReleaseDelayTooltip>
            </>
        );
    }
    return (
        <StartedReleaseDelayDescription
            appId={appId}
            releaseDelay={releaseDelay}
            isSubmission={isSubmission}
            settingsRoute={settingsRoute}
        />
    );
};

const StartedReleaseDelayDescription: React.FC<{
    appId: string;
    releaseDelay: ReleaseDelay;
    isSubmission: boolean;
    settingsRoute: string;
}> = ({ appId, releaseDelay, isSubmission, settingsRoute }) => {
    const use24HourTimeFormat = useSelector((state: RootState) => selectAppTimeFormatSetting(state, appId));
    const effectiveTimezone = useSelector((state: RootState) => selectEffectiveTimezone(state, appId));
    const delayedUntil = dayjs(releaseDelay.delayedUntil);
    const [hasDelayEnded, setHasDelayEnded] = React.useState(!delayedUntil.isAfter(dayjs()));

    React.useEffect(() => {
        const msUntilEnd = dayjs(releaseDelay.delayedUntil).diff(dayjs());
        setHasDelayEnded(msUntilEnd <= 0);
        if (msUntilEnd <= 0) return undefined;
        const timeout = setTimeout(() => setHasDelayEnded(true), msUntilEnd);
        return () => clearTimeout(timeout);
    }, [releaseDelay.delayedUntil]);

    const action = isSubmission ? 'submission' : 'release';
    const verb = isSubmission ? 'submit' : 'release';
    const formattedTime = formatDateWithTimezone(
        delayedUntil,
        'shortWithTimezone',
        effectiveTimezone,
        use24HourTimeFormat,
    );
    const isManualDelay = !!releaseDelay.requestedBy;

    if (hasDelayEnded) {
        if (!isManualDelay && !releaseDelay.automationEnabled) {
            return (
                <>
                    Delay ended at {formattedTime}. Auto-{verb} is off, {verb} manually.
                </>
            );
        }
        return (
            <>
                Delay ended at {formattedTime}. Runway will {verb} this version shortly.
            </>
        );
    }

    return (
        <>
            Pending {action}{' '}
            <ReleaseDelayTooltip
                delayMinutes={releaseDelay.delayMinutes}
                isSubmission={isSubmission}
                hasStarted
                requestedBy={releaseDelay.requestedBy}
                settingsRoute={settingsRoute}
            >
                (after {formatDelayMinutes(releaseDelay.delayMinutes)} delay)
            </ReleaseDelayTooltip>
            {!isManualDelay && !releaseDelay.automationEnabled && ` Auto-${verb} is off, ${verb} manually.`}
        </>
    );
};

export const getCalendarEventDelayInfo = (
    eventType: LifecycleEventType,
    eventDateType: ReleaseCalendarEventType,
    isReleaseSkipped: boolean,
    delayMinutes: number | undefined,
    delayHasStarted: boolean | undefined,
    isManualDelay: boolean | undefined,
    orgId: string,
    appId: string,
): React.ReactNode => {
    if (eventType === LifecycleEventType.LifecycleEventTypePreviousReleaseAccelerated) {
        if (isReleaseSkipped || eventDateType === ReleaseCalendarEventType.ReleaseCalendarEventTypeActualDate) {
            return null;
        }
        const accelerateText = 'Runway accelerates the previous version to 100%.';
        return eventDateType === ReleaseCalendarEventType.ReleaseCalendarEventTypeTargetDate ?
                `${accelerateText} Executes within 5 minutes after the target date.`
            :   accelerateText;
    }
    if (
        delayMinutes &&
        (eventType === LifecycleEventType.LifecycleEventTypeSubmit ||
            eventType === LifecycleEventType.LifecycleEventTypeRelease)
    ) {
        const isSubmission = eventType === LifecycleEventType.LifecycleEventTypeSubmit;
        const delayText = releaseDelayAfterAccelerationText(delayMinutes, isSubmission, !!delayHasStarted);
        if (isManualDelay) {
            return `${delayText}, as requested when ${isSubmission ? 'submitting' : 'releasing'}.`;
        }
        return (
            <>
                {delayText} per{' '}
                <Link to={createAppSettingsRouteWithSettingType(orgId, appId, SettingType.Automations)}>
                    automation settings
                </Link>
                .
            </>
        );
    }
    return null;
};
